import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth-context'
import TrailerLoaderDemoCard from '../components/TrailerLoaderDemoCard'
import WorkflowCloseoutCard from '../components/WorkflowCloseoutCard'
import { SupportReadOnlyNotice, useSupportReadOnlyView } from '../lib/support-session'
import { subscribeToLoadStatusChanges } from '../lib/load-status-sync'

const STATUS_COLORS: Record<string, string> = {
  POSTED: 'bg-blue-100 text-blue-800',
  CLAIMED: 'bg-cyan-100 text-cyan-800',
  ASSIGNED: 'bg-purple-100 text-purple-800',
  DRIVER_ASSIGNED: 'bg-violet-100 text-violet-800',
  IN_PROGRESS: 'bg-orange-100 text-orange-800',
  PICKED_UP: 'bg-orange-100 text-orange-800',
  IN_TRANSIT: 'bg-orange-100 text-orange-800',
  DELIVERED: 'bg-green-100 text-green-800',
  WAITING_POD: 'bg-amber-100 text-amber-800',
  POD_UPLOADED: 'bg-emerald-100 text-emerald-800',
  INVOICED: 'bg-indigo-100 text-indigo-800',
  PAID: 'bg-emerald-100 text-emerald-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
  PENDING: 'bg-yellow-100 text-yellow-800',
}

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Draft',
  POSTED: 'Posted',
  CLAIMED: 'Claimed',
  ASSIGNED: 'Driver Assigned',
  DRIVER_ASSIGNED: 'Driver Assigned',
  PICKED_UP: 'In Transit',
  IN_PROGRESS: 'In Transit',
  IN_TRANSIT: 'In Transit',
  DELIVERED: 'Delivered',
  WAITING_POD: 'Delivered',
  POD_UPLOADED: 'Delivered',
  INVOICED: 'Closed',
  PAID: 'Closed',
  COMPLETED: 'Trip Complete',
  CANCELLED: 'Cancelled',
}

function safeDateLabel(value: unknown): string {
  if (!value) return 'Not scheduled'
  const raw = String(value)
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(raw)
  if (Number.isNaN(date.getTime())) return 'Not scheduled'
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function safeDateTimeLabel(value: unknown): string {
  if (!value) return 'Not recorded'
  const date = new Date(String(value))
  if (Number.isNaN(date.getTime())) return 'Not recorded'
  return date.toLocaleString()
}

function humanizeEnum(value: unknown): string {
  return String(value ?? '').trim().toLowerCase().split('_').filter(Boolean).map((part) => part[0].toUpperCase() + part.slice(1)).join(' ') || 'Update'
}

function safeTimeLabel(value: unknown): string | null {
  if (!value) return null
  const raw = String(value).trim()
  const timeMatch = raw.match(/^(?:\d{4}-\d{2}-\d{2}[T\s])?(\d{2}):(\d{2})/)
  const date = timeMatch
    ? new Date(1970, 0, 1, Number(timeMatch[1]), Number(timeMatch[2]))
    : new Date(raw)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

function timeWindowLabel(start: unknown, end: unknown): string | null {
  const first = safeTimeLabel(start)
  const last = safeTimeLabel(end)
  if (first && last) return `${first} – ${last}`
  return first || last
}

export default function LoadDetailPage() {
  const { loadId } = useParams<{ loadId: string }>()
  const [searchParams] = useSearchParams()
  const { user, hasRole } = useAuth()
  const { isSupportReadOnlyView } = useSupportReadOnlyView()
  const viewerParam = String(searchParams.get('viewer') ?? '').trim().toLowerCase()
  const viewerMode: 'carrier' | 'driver' | 'internal' | undefined =
    viewerParam === 'carrier' || viewerParam === 'driver' || viewerParam === 'internal'
      ? (viewerParam as 'carrier' | 'driver' | 'internal')
      : undefined
  const isRestrictedViewer = viewerMode === 'carrier' || viewerMode === 'driver'

  const canDelete = !isRestrictedViewer && !isSupportReadOnlyView && hasRole('SUPER_ADMIN', 'AUX_ADMIN', 'ADMIN', 'CARRIER_ADMIN', 'DISPATCHER', 'BROKER_ADMIN')
  const canSeeInternal = !isRestrictedViewer && hasRole('SUPER_ADMIN', 'AUX_ADMIN', 'ADMIN', 'CARRIER_ADMIN', 'DISPATCHER')
  const canUseDemoAction = ['SUPER_ADMIN', 'AUX_ADMIN', 'ADMIN', 'CARRIER_ADMIN', 'DISPATCHER'].includes(user?.accountType ?? '')
  const canPlanTrailer = hasRole('DISPATCHER', 'BROKER_ADMIN', 'DEALER', 'SHIPPER', 'ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN')
  const canSeeCustomer = !isRestrictedViewer && (canSeeInternal || hasRole('BROKER', 'BROKER_ADMIN', 'DEALER', 'SHIPPER'))
  const canSeeCarrierMoney = !isRestrictedViewer && canSeeInternal
  const isDriver = hasRole('DRIVER')
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [selectedDriverId, setSelectedDriverId] = useState('')
  const [selectedCarrierId, setSelectedCarrierId] = useState('')
  const [assignmentNotes, setAssignmentNotes] = useState('')
  const [trailerNumber, setTrailerNumber] = useState('')
  const [unassignReason, setUnassignReason] = useState('')
  const [carrierOfferInput, setCarrierOfferInput] = useState('')
  const [unassignTarget, setUnassignTarget] = useState<'driver' | 'carrier' | 'both'>('driver')
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [workflowError, setWorkflowError] = useState<string | null>(null)

  const { data: load, isLoading, error } = useQuery({
    queryKey: ['load', loadId, viewerMode ?? 'default'],
    queryFn: async () => {
      const r = await api.loads.get(loadId!, { viewer: viewerMode })
      return r.data
    },
    enabled: !!loadId,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    staleTime: 0,
    refetchInterval: 5000,
  })

  const { data: invoices = [] } = useQuery({
    queryKey: ['load-detail-invoices', loadId],
    queryFn: async () => (await api.invoices.list({ loadId: loadId! })).data,
    enabled: !!loadId,
    refetchInterval: 15000,
  })

  const { data: jobs = [] } = useQuery({
    queryKey: ['load-detail-jobs'],
    queryFn: async () => (await api.jobs.list()).data,
    refetchInterval: 15000,
  })

  const relatedJob = jobs.find((job: any) => String(job.loadId ?? job.load_id) === String(load?.id))

  const carrierOrgId = (load as any)?.carrierOrgId ?? (load as any)?.carrier_org_id ?? (load as any)?.carrierId ?? null
  const isShipperOwner = !!user?.orgId && String((load as any)?.shipperOrgId ?? (load as any)?.shipper_org_id ?? '') === String(user.orgId)

  const { data: carrierDrivers = [] } = useQuery({
    queryKey: ['carrier-drivers', carrierOrgId],
    queryFn: async () => (await api.carrier.listDrivers()).data.drivers,
    enabled: canSeeInternal && !!carrierOrgId,
    refetchInterval: 30000,
  })

  const { data: assignmentOptions } = useQuery({
    queryKey: ['load-assignment-options', loadId],
    queryFn: async () => (await api.loads.assignmentOptions(loadId!)).data,
    enabled: canSeeInternal && !!loadId,
    refetchInterval: 10000,
  })

  const { data: assignmentHistory } = useQuery({
    queryKey: ['load-assignment-history', loadId],
    queryFn: async () => (await api.loads.assignmentHistory(loadId!)).data.history,
    enabled: canSeeInternal && !!loadId,
    refetchInterval: 10000,
  })

  const cancelMut = useMutation({
    mutationFn: () => api.loads.cancel(loadId!, 'Cancelled by broker'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['load', loadId] }),
  })

  const refreshAll = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['load', loadId] }),
      qc.invalidateQueries({ queryKey: ['load-detail-jobs'] }),
      qc.invalidateQueries({ queryKey: ['load-detail-invoices', loadId] }),
      qc.invalidateQueries({ queryKey: ['load-assignment-options', loadId] }),
      qc.invalidateQueries({ queryKey: ['load-assignment-history', loadId] }),
      qc.invalidateQueries({ queryKey: ['dispatch-board'] }),
      qc.invalidateQueries({ queryKey: ['carrier-loads'] }),
      qc.invalidateQueries({ queryKey: ['driver-jobs'] }),
    ])
  }

  useEffect(() => {
    if (!loadId) return
    return subscribeToLoadStatusChanges(loadId, () => {
      void qc.invalidateQueries({ queryKey: ['load', loadId] })
      void qc.invalidateQueries({ queryKey: ['load-detail-jobs'] })
      void qc.invalidateQueries({ queryKey: ['load-assignment-options', loadId] })
      void qc.invalidateQueries({ queryKey: ['load-assignment-history', loadId] })
    })
  }, [loadId, qc])

  const postLoadMut = useMutation({
    mutationFn: () => api.loads.post(loadId!, { notes: assignmentNotes || undefined }),
    onSuccess: refreshAll,
  })

  const claimMut = useMutation({
    mutationFn: () => api.loads.claim(loadId!, { carrierOrgId: carrierOrgId ?? undefined }),
    onSuccess: refreshAll,
  })

  const assignCarrierMut = useMutation({
    mutationFn: () => api.loads.assignCarrier(loadId!, {
      carrierId: selectedCarrierId || undefined,
      notes: assignmentNotes || undefined,
      carrierPayCents: carrierOfferInput ? Math.round(Number(carrierOfferInput) * 100) : undefined,
    }),
    onSuccess: refreshAll,
  })

  const assignDriverToLoadMut = useMutation({
    mutationFn: () => api.loads.assignDriver(loadId!, {
      driverUserId: selectedDriverId,
      carrierId: selectedCarrierId || assignmentOptions?.load.currentAssignment.assignedCarrierId || undefined,
      notes: assignmentNotes || undefined,
    }),
    onSuccess: refreshAll,
  })

  const dispatchTripMut = useMutation({
    mutationFn: async () => {
      const materialized = await api.dispatchLoads.materializeOrder(loadId!)
      const orderId = materialized.data.order.id
      return api.orders.upsertDispatchTrip(orderId, {
        carrierId: selectedCarrierId || undefined,
        carrierOrgId: carrierOrgId || undefined,
        driverUserId: selectedDriverId || assignmentOptions?.load.currentAssignment.driverUserId || undefined,
        trailerNumber: trailerNumber.trim() || undefined,
        assignmentNotes: assignmentNotes.trim() || undefined,
      })
    },
    onSuccess: refreshAll,
  })

  const dispatchTripNowMut = useMutation({
    mutationFn: async () => {
      const materialized = await api.dispatchLoads.materializeOrder(loadId!)
      return api.orders.dispatchTrip(materialized.data.order.id)
    },
    onSuccess: refreshAll,
    onError: (error: any) => setWorkflowError(String(error?.response?.data?.error || error?.message || 'Unable to dispatch trip.')),
  })

  const unassignMut = useMutation({
    mutationFn: () => api.loads.unassign(loadId!, {
      target: unassignTarget,
      reason: unassignReason || undefined,
    }),
    onSuccess: refreshAll,
  })

  const updateCarrierOfferMut = useMutation({
    mutationFn: () => api.loads.updateCarrierOffer(loadId!, {
      carrierOfferCents: Math.round(Number(carrierOfferInput || '0') * 100),
      notes: assignmentNotes || undefined,
    }),
    onSuccess: refreshAll,
  })

  const assignDriverMut = useMutation({
    mutationFn: () => api.jobs.assignDriver(relatedJob!.id, selectedDriverId),
    onSuccess: refreshAll,
  })

  const transitMut = useMutation({
    mutationFn: () => api.jobs.updateStatus(relatedJob!.id, 'IN_TRANSIT'),
    onSuccess: refreshAll,
  })

  const deliveredMut = useMutation({
    mutationFn: () => api.jobs.deliver(relatedJob!.id, { notes: 'Delivered from load detail workflow' }),
    onSuccess: refreshAll,
  })

  const completeMut = useMutation({
    mutationFn: () => api.jobs.complete(relatedJob!.id, {
      miles: load.estDistanceMiles,
      revenue: typeof load.customerPayCents === 'number' ? load.customerPayCents / 100 : undefined,
      carrierPay: typeof load.carrierPayCents === 'number' ? load.carrierPayCents / 100 : undefined,
    }),
    onSuccess: refreshAll,
  })

  const paidInvoice = invoices.find((invoice: any) => String(invoice.loadId ?? invoice.load_id) === String(load.id))
  const markPaidMut = useMutation({
    mutationFn: () => api.invoices.markPaid(paidInvoice!.id),
    onSuccess: refreshAll,
  })

  const deleteMut = useMutation({
    mutationFn: () => api.loads.delete(loadId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loads'] })
      navigate('/loads')
    },
    onError: (err: any) => {
      setDeleteError(err?.response?.data?.error ?? 'Failed to delete load.')
    },
  })

  if (isLoading) return <div className="container mx-auto p-6 text-gray-500">Loading…</div>
  if (error || !load) return <div className="container mx-auto p-6 text-red-600">Load not found.</div>
  const loadStatus = String(load.status ?? '').toUpperCase()
  const workflowDriverSelectReady = canSeeInternal && !!relatedJob && carrierDrivers.length > 0
  const loadMetadata = load.metadata && typeof load.metadata === 'object' ? load.metadata : {}
  const pickupWindowStart = load.pickupWindowStart ?? (load as any).pickup_window_start ?? loadMetadata.pickupWindowStart
  const pickupWindowEnd = load.pickupWindowEnd ?? (load as any).pickup_window_end ?? loadMetadata.pickupWindowEnd
  const deliveryWindowStart = load.deliveryWindowStart ?? (load as any).delivery_window_start ?? loadMetadata.deliveryWindowStart
  const deliveryWindowEnd = load.deliveryWindowEnd ?? (load as any).delivery_window_end ?? loadMetadata.deliveryWindowEnd
  const pickupNotes = load.notes ?? (typeof loadMetadata.pickupNotes === 'string' ? loadMetadata.pickupNotes : null)
  const humanStatus = STATUS_LABELS[loadStatus] ?? 'Needs attention'
  const hasDispatchTrip = Boolean((load as any).activeDispatchTripId || (load as any).active_dispatch_trip_id)
  const currentDriverName = load.assignedDriverName || (assignmentOptions?.load.currentAssignment.driverUserId ? 'Assigned driver' : null)
  const nextStep = ['DELIVERED', 'WAITING_POD', 'POD_UPLOADED', 'INVOICED', 'PAID', 'COMPLETED'].includes(loadStatus)
    ? 'Trip Complete. Review history and documents as needed.'
    : hasDispatchTrip
      ? 'Active dispatch trip. Open the trip to follow driver execution.'
      : loadStatus === 'PENDING'
    ? 'Post the load for carrier pickup.'
    : loadStatus === 'POSTED'
      ? 'Wait for a carrier to claim this load.'
      : loadStatus === 'CLAIMED'
        ? 'Assign a driver to the claimed carrier.'
        : loadStatus === 'DRIVER_ASSIGNED' || loadStatus === 'ASSIGNED'
          ? 'Create the dispatch trip.'
          : ['IN_TRANSIT', 'PICKED_UP', 'IN_PROGRESS'].includes(loadStatus)
            ? 'The driver is preparing for or completing transit.'
            : ['DELIVERED', 'WAITING_POD', 'POD_UPLOADED'].includes(loadStatus)
              ? 'Review the closeout and delivery evidence.'
              : loadStatus === 'INVOICED' || loadStatus === 'PAID'
                ? 'Transport is complete.'
                : 'Review the load details.'

  const focusSection = (id: string) => {
    const section = document.getElementById(id) as HTMLDetailsElement | null
    if (section) section.open = true
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const primaryAction = ['DELIVERED', 'WAITING_POD', 'POD_UPLOADED', 'INVOICED', 'PAID', 'COMPLETED'].includes(loadStatus)
    ? <button type="button" onClick={() => focusSection('closeout-panel')} className="rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800">View Trip History</button>
    : hasDispatchTrip
      ? <Link to={relatedJob?.id ? '/jobs/' + relatedJob.id : '/loads/' + load.id} className="rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800">Open Active Trip</Link>
      : loadStatus === 'PENDING'
    ? <button type="button" onClick={() => postLoadMut.mutate()} disabled={postLoadMut.isPending || isSupportReadOnlyView} className="rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50">{postLoadMut.isPending ? 'Posting…' : 'Post Load'}</button>
    : loadStatus === 'POSTED'
      ? <Link to="/dispatch-board" className="rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800">View claim status</Link>
        : loadStatus === 'CLAIMED'
        ? <button type="button" onClick={() => focusSection('assignment-panel')} className="rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800">Assign Driver</button>
        : (loadStatus === 'DRIVER_ASSIGNED' || loadStatus === 'ASSIGNED') && !hasDispatchTrip
          ? <button type="button" onClick={() => focusSection('assignment-panel')} className="rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800">Create Dispatch Trip</button>
          : ['IN_TRANSIT', 'PICKED_UP', 'IN_PROGRESS'].includes(loadStatus) && relatedJob?.id
            ? <Link to={`/jobs/${relatedJob.id}`} className="rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800">Open Trip</Link>
            : ['DELIVERED', 'WAITING_POD', 'POD_UPLOADED'].includes(loadStatus)
              ? <button type="button" onClick={() => focusSection('closeout-panel')} className="rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800">Review Closeout</button>
              : <button type="button" onClick={() => focusSection('details-panel')} className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50">View Load Details</button>

  const carrierCandidates = assignmentOptions?.carriers ?? []
  const driverCandidates = assignmentOptions?.drivers ?? []
  const selectedDriverEquipment = assignmentOptions?.equipment?.find((item: any) => String(item.aux_user_id) === String(selectedDriverId))

  return (
    <div className="container mx-auto p-6 max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/loads')} className="text-gray-400 hover:text-gray-700 text-sm">← Loads</button>
        <h1 className="text-xl font-bold text-gray-900">{load.loadNumber ?? load.id.slice(0, 8)}</h1>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[load.status] ?? 'bg-gray-100 text-gray-700'}`}>
          {humanStatus}
        </span>
        <details className="relative ml-auto">
          <summary className="cursor-pointer list-none rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50">More actions</summary>
          <div className="absolute right-0 z-10 mt-2 grid min-w-44 gap-2 rounded-lg border bg-white p-2 text-xs shadow-lg">
            {canPlanTrailer && <Link to={`/loads/${load.id}/trailer`} className="rounded px-2 py-1.5 text-gray-700 hover:bg-gray-50">Trailer planning</Link>}
            {loadStatus === 'POSTED' && hasRole('BROKER_ADMIN', 'ADMIN', 'AUX_ADMIN') && <button type="button" onClick={() => { if (confirm('Cancel this load?')) cancelMut.mutate() }} disabled={cancelMut.isPending || isSupportReadOnlyView} className="rounded px-2 py-1.5 text-left text-red-700 hover:bg-red-50 disabled:opacity-50">Cancel load</button>}
            {canDelete && !isDriver && <button type="button" onClick={() => { setDeleteError(null); if (confirm('Are you sure you want to delete this load?')) deleteMut.mutate() }} disabled={deleteMut.isPending || isSupportReadOnlyView} className="rounded px-2 py-1.5 text-left text-red-700 hover:bg-red-50 disabled:opacity-50">Delete load</button>}
          </div>
        </details>
      </div>

      {deleteError && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{deleteError}</div>
      )}
      {isSupportReadOnlyView && <SupportReadOnlyNotice className="mb-4" />}

      <div className="space-y-4">
        <section className="rounded-xl border border-blue-200 bg-blue-50 p-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">Current status</div>
              <h2 className="mt-1 text-2xl font-bold text-gray-900">{humanStatus}</h2>
              <p className="mt-2 max-w-xl text-sm text-gray-700">{nextStep}</p>
              {(load.assignedCarrierName || currentDriverName || hasDispatchTrip) && <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                {load.assignedCarrierName && <span>Carrier: <strong>{load.assignedCarrierName}</strong></span>}
                {currentDriverName && <span>Driver: <strong>{currentDriverName}</strong></span>}
                {hasDispatchTrip && <span>Trip: <strong>{(load as any).activeDispatchTripNumber ?? 'Active dispatch trip'}</strong></span>}
              </div>}
            </div>
            <div className="shrink-0">{primaryAction}</div>
          </div>
          <div className="mt-4 border-t border-blue-200 pt-3 text-xs text-blue-900"><span className="font-semibold">Next step:</span> {nextStep}</div>
        </section>

        <section className="rounded-xl border bg-white p-5">
          <div className="mb-4 flex items-center justify-between gap-3"><h2 className="font-semibold text-gray-800">Route summary</h2><span className="text-xs text-gray-500">Load {load.loadNumber ?? load.id.slice(0, 8)}</span></div>
          <div className="grid gap-4 text-sm sm:grid-cols-2">
            <div><span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Pickup</span><p className="mt-1 font-medium text-gray-900">{load.origin || 'Pickup location not set'}</p><p className="mt-1 text-gray-600">{safeDateLabel(load.pickupDate)}{timeWindowLabel(pickupWindowStart, pickupWindowEnd) ? ` · ${timeWindowLabel(pickupWindowStart, pickupWindowEnd)}` : ''}</p>{pickupNotes && <p className="mt-1 text-xs text-gray-500">Notes: {pickupNotes}</p>}</div>
            <div><span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Drop-off</span><p className="mt-1 font-medium text-gray-900">{load.destination || 'Drop-off location not set'}</p><p className="mt-1 text-gray-600">{safeDateLabel(load.deliveryDate)}{timeWindowLabel(deliveryWindowStart, deliveryWindowEnd) ? ` · ${timeWindowLabel(deliveryWindowStart, deliveryWindowEnd)}` : ''}</p></div>
            <div><span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Distance</span><p className="mt-1 font-semibold text-gray-900">{typeof load.estDistanceMiles === 'number' ? `${load.estDistanceMiles.toLocaleString()} mi` : 'Not calculated'}</p></div>
            {(canSeeCustomer && !isDriver && typeof load.customerPayCents === 'number') && <div><span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Customer pay</span><p className="mt-1 font-semibold text-gray-900">${(load.customerPayCents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}</p></div>}
            {(canSeeCarrierMoney && typeof load.carrierPayCents === 'number') && <div><span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Carrier pay</span><p className="mt-1 font-semibold text-gray-900">${(load.carrierPayCents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}</p></div>}
            {(canSeeInternal && typeof load.grossMarginCents === 'number') && <div><span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Margin</span><p className="mt-1 font-semibold text-gray-900">${(load.grossMarginCents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}</p></div>}
          </div>
        </section>

        {load.vehicles && load.vehicles.length > 0 && (
          <div className="bg-white rounded-xl border p-5">
            <h2 className="font-semibold text-gray-800 mb-3">Vehicles ({load.vehicles.length})</h2>
            <div className="space-y-2">
              {load.vehicles.map((v: any) => (
                <div key={v.id} className="flex justify-between items-center text-sm border rounded p-3 bg-gray-50">
                  <div>
                    <div className="font-medium text-gray-900">{[v.year, v.make, v.model].filter(Boolean).join(' ') || 'Vehicle details pending'}</div>
                    {v.vin && <div className="mt-1 font-mono text-xs text-gray-500">VIN {v.vin}</div>}
                  </div>
                  {v.color && <span className="text-gray-400 text-xs">{v.color}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {load.status === 'POSTED' && hasRole('CARRIER_ADMIN', 'ADMIN', 'AUX_ADMIN') && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-800 font-medium mb-2">Ready to accept this load?</p>
            <Link to="/carrier/loads" className="text-sm text-blue-700 underline">Go to Carrier Board to accept</Link>
          </div>
        )}

        {load.status === 'DELIVERED' && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="font-semibold text-green-800">Load Delivered</p>
            <Link to="/invoices" className="text-sm text-green-700 underline mt-1 block">View Invoice</Link>
          </div>
        )}

        <details id="closeout-panel" className={`${['DELIVERED', 'WAITING_POD', 'POD_UPLOADED', 'INVOICED', 'PAID'].includes(loadStatus) ? '' : 'border rounded-xl bg-white'} p-5`} open={['DELIVERED', 'WAITING_POD', 'POD_UPLOADED', 'INVOICED', 'PAID'].includes(loadStatus)}>
          <summary className="cursor-pointer list-none font-semibold text-gray-800">Closeout</summary>
          <div className="mt-4">
            <WorkflowCloseoutCard
              loadId={load.id}
              loadStatus={load.status}
              jobId={relatedJob?.id}
              jobStatus={relatedJob?.status}
              podUploaded={Boolean(relatedJob?.proofOfDeliveryUploadedAt || relatedJob?.proofOfDeliveryUrl)}
              invoiceId={paidInvoice?.id}
              invoiceStatus={paidInvoice?.status}
              tripStatus={(load as any).dispatchTripStatus ?? (load as any).tripStatus}
            />
          </div>
        </details>

        <details id="assignment-panel" className="bg-white rounded-xl border p-5">
          <summary className="cursor-pointer list-none font-semibold text-gray-800">Assignment &amp; advanced actions <span className="ml-2 text-xs font-normal text-gray-500">Open only when you need to change the workflow</span></summary>
          <div className="mt-4 space-y-3">
          {workflowError && <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800">{workflowError}</div>}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-semibold text-gray-800">Workflow</h2>
              <p className="text-sm text-gray-500">Move this order through claim, dispatch, transit, POD, invoicing, and payment.</p>
            </div>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[loadStatus] ?? 'bg-gray-100 text-gray-700'}`}>
              {loadStatus || 'UNKNOWN'}
            </span>
          </div>

          <fieldset disabled={isSupportReadOnlyView} className="flex flex-wrap gap-2 text-sm">
            {canSeeInternal && loadStatus === 'POSTED' && !isShipperOwner && (
              <button onClick={() => claimMut.mutate()} disabled={claimMut.isPending} className="rounded-lg border border-blue-300 px-3 py-2 text-blue-700 hover:bg-blue-50 disabled:opacity-50">
                Claim Load
              </button>
            )}
            {canSeeInternal && (loadStatus === 'CLAIMED' || loadStatus === 'POSTED') && (
              <button onClick={() => assignCarrierMut.mutate()} disabled={assignCarrierMut.isPending} className="rounded-lg border border-purple-300 px-3 py-2 text-purple-700 hover:bg-purple-50 disabled:opacity-50">
                Assign Carrier
              </button>
            )}
            {workflowDriverSelectReady && (
              <div className="flex items-center gap-2 flex-wrap">
                <select value={selectedDriverId} onChange={e => setSelectedDriverId(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-gray-700 bg-white min-w-[220px] disabled:bg-gray-50 disabled:text-gray-400">
                  <option value="">Select driver</option>
                  {carrierDrivers.map((driver: any) => (
                    <option key={driver.auxUserId} value={driver.auxUserId}>
                      {driver.name ?? driver.email ?? driver.auxUserId}
                    </option>
                  ))}
                </select>
                <button onClick={() => assignDriverMut.mutate()} disabled={!selectedDriverId || assignDriverMut.isPending} className="rounded-lg border border-violet-300 px-3 py-2 text-violet-700 hover:bg-violet-50 disabled:opacity-50">
                  Assign Driver
                </button>
                <Link to={`/carrier/drivers/${selectedDriverId}${carrierOrgId ? `?carrierId=${carrierOrgId}` : ''}`} className={`rounded-lg border border-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-50 ${!selectedDriverId ? 'pointer-events-none opacity-50' : ''}`} aria-disabled={!selectedDriverId}>
                  Assign Equipment
                </Link>
              </div>
            )}
            {relatedJob?.id && (loadStatus === 'DRIVER_ASSIGNED' || loadStatus === 'ASSIGNED' || loadStatus === 'CLAIMED') && (
              <button onClick={() => transitMut.mutate()} disabled={transitMut.isPending} className="rounded-lg border border-orange-300 px-3 py-2 text-orange-700 hover:bg-orange-50 disabled:opacity-50">
                Mark In Transit
              </button>
            )}
            {canSeeInternal && hasDispatchTrip && loadStatus === 'DRIVER_ASSIGNED' && (
              <button onClick={() => dispatchTripNowMut.mutate()} disabled={dispatchTripNowMut.isPending} className="rounded-lg border border-green-300 px-3 py-2 text-green-700 hover:bg-green-50 disabled:opacity-50">
                {dispatchTripNowMut.isPending ? 'Dispatching trip...' : 'Dispatch Trip'}
              </button>
            )}
            {relatedJob?.id && (loadStatus === 'IN_TRANSIT' || loadStatus === 'PICKED_UP' || loadStatus === 'DRIVER_ASSIGNED') && (
              <button onClick={() => deliveredMut.mutate()} disabled={deliveredMut.isPending} className="rounded-lg border border-emerald-300 px-3 py-2 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
                Mark Delivered
              </button>
            )}
            {canSeeInternal && !isSupportReadOnlyView && !(load as any).activeDispatchTripId && !(load as any).active_dispatch_trip_id && (
              <div className="flex items-center gap-2 flex-wrap basis-full pt-2 border-t">
                <input
                  value={trailerNumber}
                  onChange={e => setTrailerNumber(e.target.value)}
                  placeholder="Trailer number"
                  aria-label="Trailer number"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <button
                  onClick={() => dispatchTripMut.mutate()}
                  disabled={!(selectedDriverId || assignmentOptions?.load.currentAssignment.driverUserId) || !trailerNumber.trim() || dispatchTripMut.isPending}
                  className="rounded-lg border border-green-300 px-3 py-2 text-green-700 hover:bg-green-50 disabled:opacity-50"
                >
                  {dispatchTripMut.isPending ? 'Creating trip...' : 'Create Dispatch Trip'}
                </button>
                <span className="text-xs text-gray-500">Creates the order, stops, unit assignments, and driver job for this load.</span>
              </div>
            )}
            {relatedJob?.id && invoices.length === 0 && (loadStatus === 'WAITING_POD' || loadStatus === 'POD_UPLOADED' || loadStatus === 'DELIVERED') && (
              <button onClick={() => completeMut.mutate()} disabled={completeMut.isPending} className="rounded-lg border border-indigo-300 px-3 py-2 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
                Generate Invoice
              </button>
            )}
            {paidInvoice && paidInvoice.status !== 'PAID' && (
              <button onClick={() => markPaidMut.mutate()} disabled={markPaidMut.isPending} className="rounded-lg border border-emerald-300 px-3 py-2 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">
                Mark Paid
              </button>
            )}
          </fieldset>

          {!isSupportReadOnlyView && canUseDemoAction && (
            <TrailerLoaderDemoCard className="mt-4" />
          )}

          <div className="grid gap-2 text-xs text-gray-600 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg bg-gray-50 border p-3"><span className="block text-gray-400">1. Posted</span><span className="font-medium">Broker publishes the load.</span></div>
            <div className="rounded-lg bg-gray-50 border p-3"><span className="block text-gray-400">2. Claimed</span><span className="font-medium">Carrier takes ownership.</span></div>
            <div className="rounded-lg bg-gray-50 border p-3"><span className="block text-gray-400">3. Dispatch</span><span className="font-medium">Carrier assigns the driver.</span></div>
            <div className="rounded-lg bg-gray-50 border p-3"><span className="block text-gray-400">4. Delivery</span><span className="font-medium">Pickup, transit, POD, invoice, payment.</span></div>
          </div>
          </div>
        </details>

        {canSeeInternal && (
          <details id="details-panel" className="bg-white rounded-xl border p-5">
            <summary className="cursor-pointer list-none font-semibold text-gray-800">More details <span className="ml-2 text-xs font-normal text-gray-500">Documents, assignment audit, previews, and tools</span></summary>
            <div className="mt-4 space-y-4">
            <div>
              <h2 className="font-semibold text-gray-800">Assignment details</h2>
              <p className="text-sm text-gray-500 mt-1">Post, assign, unassign, and audit assignment changes without exposing internal finance.</p>
            </div>

            {!!assignmentOptions?.complianceWarnings?.length && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {assignmentOptions.complianceWarnings.map((warning: string) => (
                  <div key={warning}>{warning}</div>
                ))}
              </div>
            )}

            {!!assignmentOptions?.driverEquipmentWarnings?.length && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {assignmentOptions.driverEquipmentWarnings.map((warning: string) => (
                  <div key={warning}>{warning}</div>
                ))}
              </div>
            )}

            <fieldset disabled={isSupportReadOnlyView} className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Assign Carrier</label>
                  <select
                    value={selectedCarrierId}
                    onChange={(e) => setSelectedCarrierId(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                  >
                    <option value="">Select carrier</option>
                    {carrierCandidates.map((carrier: any) => (
                      <option key={carrier.id} value={carrier.id}>
                        {(carrier.legal_name || carrier.dba_name || carrier.id)} ({String(carrier.compliance_status || carrier.approval_status || 'UNKNOWN').toUpperCase()})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Assign Driver</label>
                  <select
                    value={selectedDriverId}
                    onChange={(e) => setSelectedDriverId(e.target.value)}
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                  >
                    <option value="">Select driver</option>
                    {driverCandidates
                      .filter((driver: any) => !selectedCarrierId || String(driver.carrier_org_id || '') === String(carrierCandidates.find((carrier: any) => carrier.id === selectedCarrierId)?.aux_org_id || ''))
                      .map((driver: any) => (
                        <option key={driver.id} value={driver.aux_user_id || driver.id}>
                          {driver.legal_name || [driver.first_name, driver.last_name].filter(Boolean).join(' ') || driver.aux_email || driver.aux_user_id}
                          {driver.driver_status ? ` (${String(driver.driver_status).toUpperCase()})` : ''}
                        </option>
                      ))}
                  </select>
                  {selectedDriverEquipment && (
                    <p className="mt-1 text-xs text-gray-500">Equipment: {selectedDriverEquipment.unit_label || selectedDriverEquipment.combo_key || 'Active assignment'}</p>
                  )}
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Carrier Offer ($)</label>
                  <input
                    value={carrierOfferInput}
                    onChange={(e) => setCarrierOfferInput(e.target.value.replace(/[^0-9.]/g, ''))}
                    placeholder="e.g. 1200"
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Assignment Notes</label>
                  <input
                    value={assignmentNotes}
                    onChange={(e) => setAssignmentNotes(e.target.value)}
                    placeholder="Optional assignment context"
                    className="w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2 text-sm">
                <button onClick={() => postLoadMut.mutate()} disabled={postLoadMut.isPending} className="rounded border border-blue-300 px-3 py-2 text-blue-700 hover:bg-blue-50 disabled:opacity-50">
                  {postLoadMut.isPending ? 'Posting...' : 'Post Load'}
                </button>
                <button onClick={() => assignCarrierMut.mutate()} disabled={!selectedCarrierId || assignCarrierMut.isPending} className="rounded border border-purple-300 px-3 py-2 text-purple-700 hover:bg-purple-50 disabled:opacity-50">
                  {assignCarrierMut.isPending ? 'Assigning carrier...' : 'Assign Carrier'}
                </button>
                <button onClick={() => assignDriverToLoadMut.mutate()} disabled={!selectedDriverId || assignDriverToLoadMut.isPending} className="rounded border border-violet-300 px-3 py-2 text-violet-700 hover:bg-violet-50 disabled:opacity-50">
                  {assignDriverToLoadMut.isPending ? 'Assigning driver...' : 'Assign Driver'}
                </button>
                <button onClick={() => updateCarrierOfferMut.mutate()} disabled={!carrierOfferInput || updateCarrierOfferMut.isPending} className="rounded border border-indigo-300 px-3 py-2 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
                  {updateCarrierOfferMut.isPending ? 'Saving offer...' : 'Update Carrier Offer'}
                </button>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Unassign Target</label>
                  <select value={unassignTarget} onChange={(e) => setUnassignTarget(e.target.value as 'driver' | 'carrier' | 'both')} className="w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400">
                    <option value="driver">Driver</option>
                    <option value="carrier">Carrier</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                <div className="md:col-span-2 flex items-end gap-2">
                  <input
                    value={unassignReason}
                    onChange={(e) => setUnassignReason(e.target.value)}
                    placeholder="Reason"
                    className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-400"
                  />
                  <button onClick={() => unassignMut.mutate()} disabled={unassignMut.isPending} className="rounded border border-red-300 px-3 py-2 text-red-700 hover:bg-red-50 disabled:opacity-50">
                    {unassignMut.isPending ? 'Unassigning...' : 'Unassign'}
                  </button>
                </div>
              </div>
            </fieldset>

            <div className="grid gap-2 text-sm text-gray-600 md:grid-cols-2">
              <div className="rounded border bg-gray-50 p-3">
                <div className="text-xs text-gray-500">Preview Carrier View</div>
                <Link className="text-blue-700 underline" to={`/loads/${load.id}?viewer=carrier`}>Open carrier view</Link>
              </div>
              <div className="rounded border bg-gray-50 p-3">
                <div className="text-xs text-gray-500">Preview Driver View</div>
                <Link className="text-blue-700 underline" to={`/loads/${load.id}?viewer=driver`}>Open driver view</Link>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Assignment History</h3>
              {!assignmentHistory?.length && <div className="text-xs text-gray-500">No assignment events recorded yet.</div>}
              {!!assignmentHistory?.length && (
                <div className="space-y-2">
                  {assignmentHistory.map((event: any) => (
                    <div key={event.id} className="rounded border px-3 py-2 text-xs bg-gray-50">
                      <div className="font-semibold text-gray-800">{humanizeEnum(event.event_type)}</div>
                      <div className="text-gray-600">{humanizeEnum(event.from_status)} {'→'} {humanizeEnum(event.to_status)}</div>
                      {event.notes && <div className="text-gray-600">{event.notes}</div>}
                      <div className="text-gray-400">{safeDateTimeLabel(event.created_at)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
          </details>
        )}

        <details className="bg-white rounded-xl border p-5">
          <summary className="cursor-pointer list-none font-semibold text-gray-800">Documents <span className="ml-2 text-xs font-normal text-gray-500">Rate confirmation and dispatch documents</span></summary>
          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            <Link to={`/loads/${load.id}/rate-confirmation`} className="rounded-lg border border-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-50">View Rate Confirmation</Link>
            {relatedJob?.id && <Link to={`/jobs/${relatedJob.id}/dispatch-sheet`} className="rounded-lg border border-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-50">View Dispatch Sheet</Link>}
            {!relatedJob?.id && <span className="text-xs text-gray-500">Dispatch sheet becomes available after dispatch.</span>}
          </div>
        </details>

        {/* Admin/dispatcher: Trailer Loader preview card — internal only */}
        {canSeeInternal && relatedJob?.id && <details className="bg-white rounded-xl border p-5">
          <summary className="cursor-pointer list-none font-semibold text-gray-800">Trailer planning <span className="ml-2 text-xs font-normal text-gray-500">Advanced tool</span></summary>
          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            <Link to={`/driver/jobs/${relatedJob.id}/trailer`} className="rounded-lg bg-green-700 px-4 py-2 font-semibold text-white hover:bg-green-800">Preview trailer plan</Link>
            <Link to={`/jobs/${relatedJob.id}/trailer`} className="rounded-lg border border-green-400 bg-white px-4 py-2 font-semibold text-green-800 hover:bg-green-100">Open trailer plan</Link>
          </div>
        </details>}
      </div>
    </div>
  )
}
